/**
 * Push — an FCM registration token, handed to the Worker (`D89`, `D94`).
 *
 * The backend half already exists: `apps/api` sends to FCM HTTP v1 with a
 * service account, addressed to the raw registration token this file obtains
 * and registers through `POST /me/devices`. On Android there is no relay and
 * no second token vocabulary — `@capacitor-firebase/messaging`'s `getToken()`
 * IS the FCM registration token the contract means.
 *
 * ── ONE PUSH PLUGIN, NEVER TWO ──────────────────────────────────────────────
 * `@capacitor/push-notifications` is deliberately NOT installed next to this
 * one. Both declare a service for `com.google.firebase.MESSAGING_EVENT` at the
 * same priority; Android hands each FCM event to exactly ONE of them, chosen
 * by manifest merge order. The loser's `tokenReceived` and foreground
 * listeners never fire — no error, no log — and a token refresh nobody hears
 * is a device that goes silent weeks later. Measured in the merged manifest
 * (T21), and upstream's own README says the same.
 *
 * ── THE ORDER IS FIXED, AND EVERY WRONG ORDER SUCCEEDS ─────────────────────
 * `enablePush` does, in this order:
 *   1. the channels (Android 8+) — the Worker names one on every message, and
 *      a channel the app never created does not fail: FCM delivers through the
 *      fallback channel at its importance, silent and low in the tray, and
 *      answers 200;
 *   2. the permission — `POST_NOTIFICATIONS` on Android 13+. A token obtained
 *      and registered WITHOUT it is the expensive mistake: every send returns
 *      200, the device receives the message, and Android drops it because the
 *      app may not post. So nothing is registered unless it is granted;
 *   3. the token, then `POST /me/devices` with exactly `registerDeviceInput`.
 *
 * ── REGISTERING IS THE OPT-IN (`D94`) ──────────────────────────────────────
 * A person with a device row gets push; a person without one does not. So the
 * token that was registered is remembered locally (`preferences`, key
 * `push.token`), and that record is what `refreshPush` — the boot path, which
 * never prompts — reads to decide whether this person ever asked. On Android
 * a denied permission is sticky: the second request shows no dialog, so
 * `enablePush` belongs behind a button that says what will be sent, never at
 * first launch.
 *
 * ── A TOKEN IS NOT PERMANENT ───────────────────────────────────────────────
 * FCM rotates it (app data cleared, restore onto a new phone, periodic
 * refresh). `tokenReceived` re-registers the new one through the fetcher of
 * the last `enablePush`/`refreshPush`, and `refreshPush` at every launch
 * compares the current token with the registered one. Without both, the
 * server keeps sending to a dead token and nothing anywhere reports it until
 * FCM's `UNREGISTERED` deletes the row (`D92`).
 *
 * ── THE TOKEN IS A CREDENTIAL-LIKE ADDRESS (`T-64`) ────────────────────────
 * It is live for as long as the install exists. It goes to the server and to
 * {@link PushState}, and nowhere else: never a log line. A screen that shows it
 * should show {@link describeToken}, not the string.
 *
 * Web: every export answers `unsupported` and loads nothing. Web push would
 * need the Firebase JS SDK and a service worker; not in this plan.
 */

import { registerPlugin } from "@capacitor/core";
import type { FirebaseMessagingPlugin } from "@capacitor-firebase/messaging";
import {
  DEVICE_ROUTES,
  NOTIFICATION_CHANNEL_ID,
  NOTIFICATION_CHANNELS,
  type NotificationChannelKey,
  type RegisterDeviceInput,
  type UnregisterDeviceInput,
} from "@repo/contract/notifications";
import type { PermissionState } from "./notifications";
import { isNative, toUnsubscribe, type Unsubscribe } from "./platform";
import { preferences } from "./storage";

/**
 * `ApiClient.authorizedFetch`, never `globalThis.fetch`: the client is the one
 * object allowed to spend a refresh token, and a second refresher revokes the
 * whole family. The same shape as `@repo/client/api`'s `Fetcher`.
 */
export type AuthorizedFetch = (
  path: `/${string}`,
  init?: RequestInit
) => Promise<Response>;

export type PushRegistrationStatus =
  /** Nothing attempted yet in this run. */
  | "idle"
  /** Channels, permission, token or request in flight. */
  | "working"
  /** The server accepted this token (`204`). */
  | "registered"
  /** Permission is not granted; nothing was registered. */
  | "denied"
  /** Boot path: this person never opted in, so nothing was sent. */
  | "off"
  /** A native call or the request failed — see `note`. */
  | "failed"
  /** Not inside the Android shell. */
  | "unsupported";

export interface PushState {
  /** Why `failed`/`denied`, or the HTTP status — for a diagnostics row. Never the token. */
  readonly note?: string;
  /** `POST_NOTIFICATIONS` as last read. */
  readonly permission: PermissionState;
  readonly status: PushRegistrationStatus;
  /** The current FCM token, once obtained. See the file header before displaying it. */
  readonly token: string | null;
}

export interface PushMessage {
  readonly body?: string;
  readonly data?: unknown;
  readonly title?: string;
}

export interface EnablePushOptions {
  /**
   * The user-visible name of each channel, from the live locale. Names update
   * in place on Android (importance does not), so passing translated names on
   * a later call renames them. Absent: English.
   */
  readonly channelName?: (kind: NotificationChannelKey | "default") => string;
  readonly fetch: AuthorizedFetch;
}

const TOKEN_KEY = "push.token";

/** HIGH, and permanent: Android freezes importance at channel creation, and a
 *  person can turn a channel down but never up. */
const IMPORTANCE_HIGH = 4;

const ENGLISH_CHANNEL_NAMES: Record<
  NotificationChannelKey | "default",
  string
> = {
  // The manifest-level fallback keeps the product name, untranslated.
  checkin: "Morning check-in",
  default: "My Pocket Gym",
  reminder: "Training reminders",
  session: "Session summary",
  trainer: "Trainer replies",
};

// ── State, observable by a screen (`useSyncExternalStore`-shaped) ──────────

let state: PushState = {
  permission: isNative() ? "prompt" : "unsupported",
  status: isNative() ? "idle" : "unsupported",
  token: null,
};
const listeners = new Set<(next: PushState) => void>();

function setState(patch: Partial<PushState>): void {
  // A note belongs to the transition that wrote it, so it is dropped unless
  // this patch carries one.
  const { note: _previousNote, ...rest } = state;
  state = { ...rest, ...patch };
  for (const listener of listeners) {
    listener(state);
  }
}

/** The current snapshot. Stable identity until something changes. */
export function getPushState(): PushState {
  return state;
}

/** Subscribe to every change of {@link getPushState}. */
export function subscribePush(
  listener: (next: PushState) => void
): Unsubscribe {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Enough of a token to tell two apart on a screen, and not enough to send to
 * it: the first 8 characters and the length.
 */
export function describeToken(token: string | null): string {
  return token === null
    ? "none"
    : `${token.slice(0, 8)}… (${token.length} chars)`;
}

// ── The plugin, loaded only on the device ──────────────────────────────────

/*
 * ── THE PLUGIN IS REGISTERED HERE, NOT IMPORTED FROM ITS PACKAGE ───────────
 * The package's entry registers `FirebaseMessaging` with a WEB fallback that
 * imports the Firebase JS SDK (`firebase/messaging`), an optional peer this
 * app does not install. Importing the entry — even lazily — makes the
 * bundler resolve that chain, and `vite build` fails on it. The native proxy
 * is all the app uses, and every export above short-circuits on the web
 * before reaching it, so this registers the same name WITHOUT a web
 * implementation and takes only the types from the package. Nothing of
 * Firebase reaches the web bundle. `cap sync` still finds the plugin through
 * `package.json`, which is what registers its Java side.
 */
let registered: FirebaseMessagingPlugin | null = null;

function plugin(): Promise<{ FirebaseMessaging: FirebaseMessagingPlugin }> {
  registered ??= registerPlugin<FirebaseMessagingPlugin>("FirebaseMessaging");
  return Promise.resolve({ FirebaseMessaging: registered });
}

function normalise(value: string): PermissionState {
  if (value === "granted" || value === "denied") {
    return value;
  }
  return "prompt";
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function ensureChannels(
  name: (kind: NotificationChannelKey | "default") => string
): Promise<void> {
  const { FirebaseMessaging } = await plugin();
  const kinds = Object.keys(NOTIFICATION_CHANNELS) as NotificationChannelKey[];
  const channels = [
    { id: NOTIFICATION_CHANNEL_ID, name: name("default") },
    ...kinds.map((kind) => ({
      id: NOTIFICATION_CHANNELS[kind],
      name: name(kind),
    })),
  ];
  // Idempotent: re-creating an existing id updates its name and nothing else.
  await Promise.all(
    channels.map((channel) =>
      FirebaseMessaging.createChannel({
        ...channel,
        importance: IMPORTANCE_HIGH,
      })
    )
  );
}

async function send(
  fetch: AuthorizedFetch,
  method: "DELETE" | "POST",
  body: RegisterDeviceInput | UnregisterDeviceInput
): Promise<Response> {
  return await fetch(DEVICE_ROUTES.devices, {
    body: JSON.stringify(body),
    headers: { "content-type": "application/json" },
    method,
  });
}

// The fetcher the refresh listener re-registers through: the last one a
// caller handed in. Null until then, and cleared by `disablePush`.
let activeFetch: AuthorizedFetch | null = null;
let refreshListener: Unsubscribe | null = null;

async function register(
  fetch: AuthorizedFetch,
  token: string
): Promise<PushState> {
  const previous = await preferences.get(TOKEN_KEY).catch(() => null);
  const body: RegisterDeviceInput = { platform: "android", token };
  const response = await send(fetch, "POST", body);
  if (!response.ok) {
    setState({
      note: `POST /me/devices ${response.status}`,
      status: "failed",
      token,
    });
    return state;
  }
  await preferences.set(TOKEN_KEY, token);
  if (previous !== null && previous !== token) {
    // The rotated-out token. Best effort: if this fails the server still
    // drops the row on FCM's first UNREGISTERED answer (`D92`).
    await send(fetch, "DELETE", { token: previous }).catch(() => undefined);
  }
  setState({
    note: `POST /me/devices ${response.status}`,
    status: "registered",
    token,
  });
  return state;
}

function listenForRotation(): void {
  if (refreshListener !== null) {
    return;
  }
  refreshListener = toUnsubscribe(
    plugin().then(({ FirebaseMessaging }) =>
      FirebaseMessaging.addListener("tokenReceived", ({ token }) => {
        const fetch = activeFetch;
        if (fetch === null || token === state.token) {
          return;
        }
        register(fetch, token).catch((error: unknown) => {
          setState({ note: describe(error), status: "failed", token });
        });
      })
    )
  );
}

/**
 * The opt-in, behind a button. The ONLY function here that may prompt.
 * Never throws: every outcome is a {@link PushState}.
 */
export async function enablePush(
  options: EnablePushOptions
): Promise<PushState> {
  if (!isNative()) {
    return state;
  }
  activeFetch = options.fetch;
  setState({ status: "working" });
  try {
    await ensureChannels(
      options.channelName ?? ((kind) => ENGLISH_CHANNEL_NAMES[kind])
    );
    const { FirebaseMessaging } = await plugin();
    const current = normalise(
      (await FirebaseMessaging.checkPermissions()).receive
    );
    const permission =
      current === "granted"
        ? current
        : normalise((await FirebaseMessaging.requestPermissions()).receive);
    if (permission !== "granted") {
      setState({
        note: `permission ${permission}`,
        permission,
        status: "denied",
      });
      return state;
    }
    setState({ permission });
    const { token } = await FirebaseMessaging.getToken();
    // Recorded BEFORE the listener exists: the first token also arrives as a
    // `tokenReceived` event, and without this the listener sees an unknown
    // token and posts it a second time (measured on the phone, T21).
    setState({ token });
    listenForRotation();
    return await register(options.fetch, token);
  } catch (error) {
    setState({ note: describe(error), status: "failed" });
    return state;
  }
}

/**
 * The boot path, after sign-in is known. Never prompts. Re-registers only if
 * this person opted in before (a stored token) and the permission still
 * holds, which also moves the server's `last_seen_at` and catches a token
 * that rotated while the app was not running.
 */
export async function refreshPush(
  fetch: AuthorizedFetch,
  channelName?: (kind: NotificationChannelKey | "default") => string
): Promise<PushState> {
  if (!isNative()) {
    return state;
  }
  activeFetch = fetch;
  try {
    const stored = await preferences.get(TOKEN_KEY);
    const { FirebaseMessaging } = await plugin();
    const permission = normalise(
      (await FirebaseMessaging.checkPermissions()).receive
    );
    if (stored === null) {
      setState({ permission, status: "off" });
      return state;
    }
    if (permission !== "granted") {
      // Revoked in system settings. The row stays until the person turns
      // push off in the app or FCM retires the token; sends meanwhile are
      // dropped by Android, which is the person's own choice.
      setState({
        note: `permission ${permission}`,
        permission,
        status: "denied",
      });
      return state;
    }
    setState({ permission, status: "working" });
    // ── EVERY BOOT, NOT ONLY THE OPT-IN (`D192`) ──────────────────────────
    // A channel added after the person turned push on — `trainer`, for the
    // replies nobody watched arrive — would otherwise never exist on their
    // phone, and FCM would drop those pushes onto the default channel at its
    // importance. Re-creating a channel is idempotent: it renames, nothing
    // else.
    await ensureChannels(
      channelName ?? ((kind) => ENGLISH_CHANNEL_NAMES[kind])
    );
    const { token } = await FirebaseMessaging.getToken();
    // Recorded BEFORE the listener exists: the first token also arrives as a
    // `tokenReceived` event, and without this the listener sees an unknown
    // token and posts it a second time (measured on the phone, T21).
    setState({ token });
    listenForRotation();
    return await register(fetch, token);
  } catch (error) {
    setState({ note: describe(error), status: "failed" });
    return state;
  }
}

/**
 * The opt-out. Tells the server first and forgets locally ONLY if it
 * answered: clearing a record whose server row is still alive would leave
 * push arriving with the switch off and nothing on the device able to name
 * the row. Call it BEFORE `ApiClient.signOut()`, while the credential works.
 */
export async function disablePush(fetch: AuthorizedFetch): Promise<PushState> {
  if (!isNative()) {
    return state;
  }
  try {
    const stored = await preferences.get(TOKEN_KEY);
    if (stored !== null) {
      const response = await send(fetch, "DELETE", { token: stored });
      if (!response.ok) {
        setState({
          note: `DELETE /me/devices ${response.status}`,
          status: "failed",
        });
        return state;
      }
    }
    await preferences.remove(TOKEN_KEY);
    activeFetch = null;
    refreshListener?.();
    refreshListener = null;
    const { FirebaseMessaging } = await plugin();
    // A new token next time, so a stale copy anywhere is dead.
    await FirebaseMessaging.deleteToken();
    setState({ status: "off", token: null });
    return state;
  } catch (error) {
    setState({ note: describe(error), status: "failed" });
    return state;
  }
}

/**
 * Drop the local record without calling the server — the session-EXPIRED
 * door, where there is no credential left to authenticate a DELETE. The next
 * person to sign in here re-registers the token onto their own account.
 */
export async function forgetPush(): Promise<void> {
  activeFetch = null;
  await preferences.remove(TOKEN_KEY).catch(() => undefined);
  setState({ status: isNative() ? "off" : "unsupported" });
}

/**
 * A push that arrived while the app was in the FOREGROUND. Android shows
 * nothing by itself in that case — the caller decides (a banner, or
 * `scheduleNotification`). In the background the system tray shows it and
 * this does not fire, except for data-only messages. Web: never fires.
 */
export function onPushReceived(
  listener: (message: PushMessage) => void
): Unsubscribe {
  if (!isNative()) {
    return () => undefined;
  }
  return toUnsubscribe(
    plugin().then(({ FirebaseMessaging }) =>
      FirebaseMessaging.addListener(
        "notificationReceived",
        ({ notification }) => {
          listener({
            body: notification.body,
            data: notification.data,
            title: notification.title,
          });
        }
      )
    )
  );
}

/** The person tapped a push in the tray. Web: never fires. */
export function onPushOpened(
  listener: (message: PushMessage) => void
): Unsubscribe {
  if (!isNative()) {
    return () => undefined;
  }
  return toUnsubscribe(
    plugin().then(({ FirebaseMessaging }) =>
      FirebaseMessaging.addListener(
        "notificationActionPerformed",
        ({ notification }) => {
          listener({
            body: notification.body,
            data: notification.data,
            title: notification.title,
          });
        }
      )
    )
  );
}
